import React from 'react';
import { Badge } from 'reactstrap';

import dayjs, { dateInputFieldFormat } from '~libs/dayjs';

import { SahRegisteredServiceType } from '~support-at-home/models/sahProviderSummary';

// A registration sits in one of three states, not two. Returning the state rather than a boolean
// is what stops a not-yet-effective registration being labelled "Expired": both fall outside the
// validity window, but they fail it in opposite temporal directions and a caller handed a bare
// `false` has no way to tell them apart.
export type RegistrationStatus = 'registered' | 'not-yet-effective' | 'expired';

// The window itself is the row's own start/end dates, the same date-filter logic the AC3 warning
// uses server-side (start_date null-or-past, end_date null-or-future), not a presence check.
export const registrationStatus = (row: SahRegisteredServiceType): RegistrationStatus => {
  // Local calendar date, not UTC. `new Date().toISOString()` is always UTC, so in AEST/AEDT
  // (UTC+10/+11) it reports YESTERDAY from local midnight until 10-11am. That inverts both
  // comparisons below for the first hours of every Australian business day: a service type
  // starting today would read as not yet effective, and one that lapsed yesterday would still
  // read as Registered. `start_date`/`end_date` are plain calendar dates from Services Australia,
  // so they must be compared against a calendar date derived the same way the rest of the repo
  // derives "today".
  const today = dayjs().format(dateInputFieldFormat);

  // Order matters only for a malformed row where start_date is after end_date, which Services
  // Australia should never send. "Not yet effective" wins there because a registration that has
  // not begun cannot meaningfully have lapsed.
  if (row.start_date && row.start_date > today) return 'not-yet-effective';
  if (row.end_date && row.end_date < today) return 'expired';
  return 'registered';
};

// Retained as the boolean question most callers actually ask: is this registration usable right
// now. Deliberately answers false for both non-registered states.
export const isCurrentlyActiveServiceType = (row: SahRegisteredServiceType): boolean =>
  registrationStatus(row) === 'registered';

const REGISTRATION_BADGES: Record<RegistrationStatus, { color: string; label: string }> = {
  registered: { color: 'success', label: 'Registered' },
  // `info` rather than a warning colour: a registration that starts later is correct data, not a
  // problem the user has to act on.
  'not-yet-effective': { color: 'info', label: 'Not yet effective' },
  expired: { color: 'secondary', label: 'Expired' },
};

// `renderReadOnly`-compatible mapper for the "Status" column on the Registered Services table, and
// for the registration column on SaH Service Associations. Shared on purpose, so the two screens
// cannot drift on what any of the three states means.
export const renderRegisteredServiceStatusBadge = (_value: unknown, row: SahRegisteredServiceType) => {
  const { color, label } = REGISTRATION_BADGES[registrationStatus(row)];
  return <Badge color={color}>{label}</Badge>;
};
